import React, { useEffect, useState } from 'react';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { HandleError } from '../../Common/ErrorHandling/ErrorHelper';
import { useNotify } from '../../Common/snackbarHooks';
import { getBusinessCategorySalaries, updateBusinessCategorySalaries } from '../exportApi';
import { BusinessCategorySalary } from '../types';

const businessCategoryNames: Record<BusinessCategorySalary['business_category'], string> = {
  lss: 'LSS',
  home_visit: 'Hembesök',
};

const roleNames: Record<BusinessCategorySalary['role'], string> = {
  participant: 'D',
  team_leader: 'TL',
};

const salaryName = (salary: BusinessCategorySalary): string =>
  `${businessCategoryNames[salary.business_category]}-${roleNames[salary.role]}`;

// 94.07 -> "94,07", 120 -> "120"
const formatHourlyWage = (hourlyWage: number): string =>
  new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 2 }).format(hourlyWage);

// Accepts a decimal comma, returns NaN for anything that is not a number
const parseHourlyWage = (value: string): number => (value.trim() === '' ? NaN : Number(value.replace(',', '.')));

// The base hourly wages of LSS and home visit teams, shown as text and changed in a dialog
const BusinessCategorySalaries: React.FC = () => {
  const { notifyError, notifySuccess } = useNotify();
  const [salaries, setSalaries] = useState<BusinessCategorySalary[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [values, setValues] = useState<Record<number, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    getBusinessCategorySalaries()
      .then(({ data }) => setSalaries(data.data))
      .catch(() => notifyError('Det gick inte att hämta lönerna per verksamhetskategori'));
  }, []); // eslint-disable-line

  const openDialog = (): void => {
    // Without thousands separators, which the input would not parse: 94.07 -> "94,07"
    setValues(Object.fromEntries(salaries.map((salary) => [salary.id, String(salary.hourly_wage).replace('.', ',')])));
    setIsDialogOpen(true);
  };

  const invalidIds = salaries
    .filter((salary) => {
      const hourlyWage = parseHourlyWage(values[salary.id] ?? '');
      return Number.isNaN(hourlyWage) || hourlyWage <= 0;
    })
    .map((salary) => salary.id);

  const save = (): void => {
    setIsSaving(true);
    updateBusinessCategorySalaries(
      salaries.map((salary) => ({ id: salary.id, hourly_wage: parseHourlyWage(values[salary.id]) }))
    )
      .then(({ data }) => {
        setSalaries(data.data);
        setIsDialogOpen(false);
        notifySuccess('Lönerna har uppdaterats');
      })
      .catch((error) => notifyError(HandleError(error.response?.data) || 'Det gick inte att uppdatera lönerna'))
      .finally(() => setIsSaving(false));
  };

  return (
    <div className="pt-10 px-10 pb-10">
      <div className="flex justify-between">
        <h2 className="flex items-center text-xl text-neutral-600">
          <span className="font-bold">Löner per verksamhetskategori</span>
        </h2>
      </div>

      <p className="pt-3 text-neutral-600">
        Ange grundlön för respektive verksamhetskategori exklusive semesterersättning. Anställda i de teamen får lönen
        för alla lönearter. Standardlöner för äldreboende sätts direkt i Crona.
      </p>

      <div className="flex flex-wrap items-center gap-x-8 gap-y-2 pt-4 text-neutral-600">
        {salaries.map((salary) => (
          <span key={salary.id}>
            <span className="font-medium">{salaryName(salary)}:</span> {formatHourlyWage(salary.hourly_wage)} kr/tim
          </span>
        ))}
        <Button variant="outlined" color="primary" onClick={openDialog} disabled={salaries.length === 0}>
          Ändra
        </Button>
      </div>

      <Dialog open={isDialogOpen} onClose={(): void => setIsDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Löner per verksamhetskategori</DialogTitle>
        <DialogContent>
          {salaries.map((salary) => (
            <TextField
              key={salary.id}
              fullWidth
              margin="dense"
              label={`${salaryName(salary)} (kr/tim)`}
              value={values[salary.id] ?? ''}
              onChange={(event): void => setValues({ ...values, [salary.id]: event.target.value })}
              error={invalidIds.includes(salary.id)}
              helperText={invalidIds.includes(salary.id) ? '* Ange ett belopp större än 0' : undefined}
              inputProps={{ inputMode: 'decimal' }}
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={(): void => setIsDialogOpen(false)} fullWidth color="error" variant="contained">
            Avbryt
          </Button>
          <Button
            onClick={save}
            fullWidth
            color="primary"
            variant="contained"
            disabled={isSaving || invalidIds.length > 0}
          >
            Spara
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default BusinessCategorySalaries;
